import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  Input,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { HsMediaGalleryComponent } from '@hsetu/ngui-views';
import {
  HsButtonModule,
  HsCardModule,
  HsDataSource,
  HsIconModule,
  HsMedia,
  HsMenuModule,
  HsPaginationComponent,
} from '@hsetu/ngui-components';
import { MediaService } from '@modules/media/services/media.service';
import { MediaMapper } from '@modules/media/mappers/media.mapper';
import { mediaLoader } from './kfm-media.loader';
import { GalleryItem } from '@modules/media/models/gallery-item.model';
import { HsLayerDirective } from '@hsetu/ngui-resources';
import { DomSanitizer } from '@angular/platform-browser';
import { ICON_MEDIA, ICON_PLUS } from '@shared/ui/icons/icon-constants';
import { Media } from '@modules/media/models/media.model';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import {
  ADD_FROM_CAMERA_LABEL,
  ADD_FROM_GALLERY_LABEL,
  NEW_MEDIA_LABEL,
} from '@modules/media/i18n/label-text-constants';

@Component({
  selector: 'kfm-media-gallery',
  imports: [
    CommonModule,
    HsMediaGalleryComponent,
    HsPaginationComponent,
    HsButtonModule,
    HsCardModule,
    HsIconModule,
    HsLayerDirective,
    HsMenuModule,
  ],
  templateUrl: './kfm-media-gallery.component.html',
  host: { class: 'kfm-media-gallery' },
})
export class KfmMediaGalleryComponent implements AfterViewInit {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _guidGenerator = inject(GuidGeneratorService);

  protected readonly mediaService = inject(MediaService);
  protected readonly mapper = new MediaMapper();

  protected readonly icon = this._sanitizer.bypassSecurityTrustHtml(ICON_MEDIA);
  protected readonly iconNew =
    this._sanitizer.bypassSecurityTrustHtml(ICON_PLUS);

  protected readonly fromGalleryLabel = ADD_FROM_GALLERY_LABEL;
  protected readonly fromCameraLabel = ADD_FROM_CAMERA_LABEL;
  protected readonly newMediaLabel = NEW_MEDIA_LABEL;

  protected readonly dataSources = signal<Map<string, GalleryItem>>(
    new Map<string, GalleryItem>(),
  );

  @ViewChildren('mediaItem')
  public galleryComponents!: QueryList<HsMediaGalleryComponent>;

  @Input()
  public isEditable = true;

  @Input()
  public set galleries(value: Map<string, GalleryItem>) {
    if (!value) {
      this.dataSources.set(new Map<string, GalleryItem>());
    } else {
      const ds = new Map<string, GalleryItem>();

      value.forEach((value, key) => {
        const loader = async ({ pageIndex, pageSize }) => {
          return mediaLoader.call(this, {
            ownerKey: key,
            pageIndex,
            pageSize,
          });
        };

        const dataSource = new HsDataSource<HsMedia>(loader);

        ds.set(key, {
          title: value.title,
          type: value.type,
          dataSource: dataSource,
        });
      });

      this.dataSources.set(ds);
    }
  }

  ngAfterViewInit(): void {
    this.galleryComponents.changes.subscribe(() => {
      this.afterRendered();
    });

    // initial render
    this.afterRendered();
  }

  afterRendered() {
    this.dataSources().forEach(async (value) => {
      if (value.dataSource) {
        await value.dataSource.load();
      }
    });
  }

  protected async onAddFromGalleryClicked(
    ownerKey: string,
    item: GalleryItem,
  ): Promise<void> {
    // Open gallery picker dialog
    const files = await this.openMultipleImagePicker();

    if (files && files.length > 0) {
      // Map files to Media models
      const mediaToCreate = files.map((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        return new Promise<Media>((resolve) => {
          reader.onload = () => {
            const base64String = (reader.result as string).split(',')[1];
            const media: Media = {
              mimeType: file.type,
              type: item.type,
              content: base64String,
              infoDesc: file.name,
              uId: this._guidGenerator.emptyUUID,
              tenantUId: this._guidGenerator.emptyUUID,
              userUId: this._guidGenerator.emptyUUID,
              stampCreate: new Date().toISOString(),
              syncType: 0,
              concurrencyIdentificator: this._guidGenerator.emptyUUID,
              concurrencyStamp: new Date().toISOString(),
              ownerKey: ownerKey,
            };
            resolve(media);
          };
        });
      });

      const result = await this.mediaService.createMultiple(
        await Promise.all(mediaToCreate),
      );

      if (result && item.dataSource) {
        item.dataSource.load();
      }
    }
  }

  // protected async onAddFromCameraClicked(
  //   ownerKey: string,
  //   type: number,
  // ): Promise<void> {
  //   // Open camera capture dialog
  // }

  private openMultipleImagePicker(): Promise<File[]> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;

      input.onchange = () => {
        resolve(input.files ? Array.from(input.files) : []);
      };

      input.click();
    });
  }
}
